import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Header from "@/components/Header";
import { apiClient } from "@/lib/api";
import useSEO from "@/lib/useSEO";
import {
  TrendingUp, Home, Lightbulb, Cog, Code, GraduationCap,
  Megaphone, DollarSign, Image as ImageIcon, Zap, Sparkles,
  ArrowRight,
} from "lucide-react";

const ICON_MAP = {
  TrendingUp, Home, Lightbulb, Cog, Code, GraduationCap,
  Megaphone, DollarSign, Image: ImageIcon, Zap,
};

export default function TopicsIndex() {
  const [focuses, setFocuses] = useState([]);
  const [loading, setLoading] = useState(true);

  useSEO({
    title: "Learn AI by doing — 10 paths, 60+ challenges · PromptDojo",
    description: "Five-minute AI challenges across sales, real estate, marketing, coding, and more. Real prompts, graded by Claude Sonnet 4.5. Earn your AI Black Belt.",
    canonical: "https://aipromptdojo.com/topics",
    ogImage: "https://aipromptdojo.com/api/og/UNKNOWN.png",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: focuses.map((f, idx) => ({
        "@type": "ListItem",
        position: idx + 1,
        url: `https://aipromptdojo.com/topics/${f.seo_topic_slug}`,
        name: f.seo_topic_h1,
      })),
    },
  });

  useEffect(() => {
    (async () => {
      try {
        const r = await apiClient.get("/seo/focuses");
        setFocuses(r.data?.focuses || []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <div className="App min-h-screen">
      <Header />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12 md:py-16 pb-28 md:pb-16">
        <span className="kicker">Learn AI by doing</span>
        <h1 className="font-display text-5xl md:text-6xl mt-3 leading-[1.02] tracking-tight">
          Pick a path. Earn your belt.
        </h1>
        <p className="text-[var(--ink-2)] mt-4 leading-relaxed text-lg max-w-2xl">
          Ten outcome-driven paths. Sixty+ challenges. Real prompts you&apos;ll use at work tomorrow,
          graded by real AI in real time.
        </p>

        {loading ? (
          <div className="mt-10 text-center"><span className="spinner" /></div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-10">
            {focuses.map((f) => {
              const Icon = ICON_MAP[f.icon] || Sparkles;
              return (
                <Link
                  key={f.slug}
                  to={`/topics/${f.seo_topic_slug}`}
                  className="brut p-6 group transition-all hover:-translate-y-0.5 hover:shadow-lg block"
                  style={{ borderTop: `3px solid ${f.accent}` }}
                  data-testid={`topic-card-${f.slug}`}
                >
                  <div className="flex items-start gap-3">
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center flex-shrink-0"
                      style={{ background: f.accent }}
                    >
                      <Icon size={20} color="#fff" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xl leading-none">{f.emoji}</span>
                        <span className="font-display text-lg leading-tight">{f.seo_topic_h1}</span>
                      </div>
                      <div className="text-xs text-[var(--ink-3)] mt-1">{f.subtopic_count} challenges</div>
                    </div>
                  </div>
                  <p className="text-sm text-[var(--ink-2)] mt-3 leading-relaxed line-clamp-3">
                    {f.seo_topic_blurb}
                  </p>
                  <div className="mt-4 inline-flex items-center gap-1 text-sm font-medium text-[var(--accent)] group-hover:gap-2 transition-all">
                    Explore <ArrowRight size={13} />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {/* CTA banner */}
        <div
          className="mt-14 brut-lg p-8 md:p-10 text-center"
          style={{ background: "linear-gradient(135deg, #0F1419 0%, #1E293B 100%)", color: "#fff" }}
        >
          <span className="kicker" style={{ color: "#10B981" }}>Begin your training</span>
          <h2 className="font-display text-3xl md:text-4xl mt-2 leading-tight">
            Sharpen your AI skills, one challenge at a time.
          </h2>
          <p className="text-white/70 mt-3 max-w-lg mx-auto">
            Pick a focus, ship a prompt, get graded by Claude Sonnet 4.5 — in under 5 minutes.
          </p>
          <Link to="/skill-check" className="btn-duo mt-6 inline-flex" data-testid="topics-cta-signup">
            Start My Skill Check <ArrowRight size={14} />
          </Link>
        </div>
      </main>
    </div>
  );
}
